import { useQuery } from "@tanstack/react-query";
import { Layers, Minus, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { api, ApiError, type ProductItem } from "@/lib/api";
import { StructuralLockNote } from "./ProductSheet";
import { fmtNumber } from "@/lib/format";
import { useTenant } from "./AppShell";
import { Badge, Button, cx, Input } from "./ui";

import { t } from "@/lib/i18n";
import { serverText } from "@/lib/server-text";
interface Line {
  productId: string;
  sku: string;
  name: string;
  quantity: number;
}

/**
 * Stückliste eines Artikels: Komponenten mit Mengen, Set-Bestand nach Engpass,
 * Hinweis "Enthalten in" bei Komponenten. Wird separat von den Stammdaten gespeichert.
 */
export function BundleEditor({ p, onSaved }: { p: ProductItem; onSaved: () => void }) {
  const { tenantId } = useTenant();
  const initial: Line[] = useMemo(
    () => (p.bundle?.components ?? []).map((c) => ({ productId: c.productId, sku: c.sku, name: p.bundleStock?.components.find((x) => x.productId === c.productId)?.name ?? c.sku, quantity: c.quantity })),
    [p],
  );
  const [lines, setLines] = useState<Line[]>(initial);
  const [editing, setEditing] = useState(initial.length > 0);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(lines.map((l) => [l.productId, l.quantity])) !== JSON.stringify(initial.map((l) => [l.productId, l.quantity]));
  const isComponent = (p.usedInBundles?.length ?? 0) > 0;

  const results = useQuery({
    queryKey: ["bundle-search", tenantId, search],
    queryFn: () => api.products.list(tenantId, { search, pageSize: 8, status: "active" }),
    enabled: search.trim().length >= 2,
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.products.setBundle(tenantId, p.id, lines.length ? lines.map((l) => ({ productId: l.productId, quantity: l.quantity })) : null);
      toast.success(lines.length ? "Stückliste gespeichert" : "Stückliste entfernt", { description: lines.length ? "Komponenten werden zuerst ans Lager übertragen, danach das Set." : undefined });
      onSaved();
    } catch (e) {
      toast.error(e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Speichern fehlgeschlagen") : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const setQty = (id: string, q: number) => setLines((ls) => ls.map((l) => (l.productId === id ? { ...l, quantity: Math.max(1, Math.min(999, q)) } : l)));
  const add = (c: ProductItem) => {
    if (c.id === p.id) return toast.error("Ein Set kann sich nicht selbst enthalten.");
    if (c.bundle?.components.length) return toast.error(`${c.sku} ist selbst ein Set – verschachtelte Sets sind nicht möglich.`);
    setLines((ls) => (ls.some((l) => l.productId === c.id) ? ls.map((l) => (l.productId === c.id ? { ...l, quantity: l.quantity + 1 } : l)) : [...ls, { productId: c.id, sku: c.sku, name: c.name, quantity: 1 }]));
    setSearch("");
  };

  return (
    <section>
      <div className="flex items-baseline mb-2.5">
        <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide flex items-center gap-1.5"><Layers className="size-3.5" /> {t("Set / Stückliste")}</h3>
        {p.bundleStock && !dirty && (
          <span className="ml-auto text-[12px] text-fg-muted">
            {t("Verfügbar als Set:")} <span className={cx("font-semibold tabular-nums", p.bundleStock.possibleSets === 0 ? "text-danger" : "text-fg")}>{fmtNumber(p.bundleStock.possibleSets)}</span>
            {p.bundleStock.bottleneck && p.bundleStock.components.length > 1 && <> {t("· Engpass")} {p.bundleStock.bottleneck}</>}
          </span>
        )}
      </div>

      {/* Komponente in anderen Sets */}
      {isComponent && (
        <div className="mb-3 rounded-xl border border-border bg-surface-2/50 p-3 text-[12.5px]">
          <p className="text-fg-muted">{t("Dieser Artikel ist Bestandteil von:")}</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {p.usedInBundles!.map((b) => (
              <Link key={b.id} to={`/products?open=${b.id}`} className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg border border-border bg-surface hover:border-border-strong">
                <span className="font-mono text-[11.5px]">{b.sku}</span> <span className="text-fg-subtle">{b.quantity}×</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {p.structuralLock?.locked && <div className="mb-3"><StructuralLockNote reasons={p.structuralLock.reasons.map((r) => serverText(r))} what={t("Stückliste")} /></div>}
      {p.structuralLock?.locked ? (
        lines.length > 0 && (
          <ul className="rounded-xl border border-border divide-y divide-border">
            {lines.map((l) => (
              <li key={l.productId} className="flex items-center gap-2 px-3 py-2">
                <Link to={`/products?open=${l.productId}`} className="min-w-0 flex-1 truncate text-[13px] font-medium hover:underline">{l.name}</Link>
                <span className="font-mono text-[11.5px] text-fg-muted">{l.sku}</span>
                <span className="w-10 text-right tabular-nums font-medium text-[13px]">{l.quantity}×</span>
              </li>
            ))}
          </ul>
        )
      ) : !editing ? (
        !isComponent && (
          <button onClick={() => setEditing(true)} className="w-full rounded-xl border border-dashed border-border-strong p-4 text-left hover:bg-surface-2/50">
            <span className="font-medium text-[13px] flex items-center gap-1.5"><Plus className="size-4" /> {t("Zu einem Set machen")}</span>
            <span className="block text-[12px] text-fg-muted mt-0.5">{t("Z. B. Geschenkset aus mehreren Einzelartikeln – das Lager kommissioniert die Komponenten.")}</span>
          </button>
        )
      ) : (
        <div className="space-y-2">
          {lines.length > 0 && (
            <ul className="rounded-xl border border-border divide-y divide-border">
              {lines.map((l) => {
                const s = p.bundleStock?.components.find((c) => c.productId === l.productId);
                return (
                  <li key={l.productId} className="flex items-center gap-2 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <Link to={`/products?open=${l.productId}`} className="block truncate text-[13px] font-medium hover:underline">{l.name}</Link>
                      <span className="font-mono text-[11.5px] text-fg-muted">{l.sku}</span>
                      {s && !dirty && <span className="ml-2 text-[11.5px] text-fg-subtle">{fmtNumber(s.available)} {t("verfügbar")}</span>}
                    </div>
                    {s && !dirty && p.bundleStock?.bottleneck === l.sku && lines.length > 1 && <Badge tone="warning">{t("Engpass")}</Badge>}
                    <div className="flex items-center rounded-lg border border-border">
                      <button onClick={() => setQty(l.productId, l.quantity - 1)} disabled={l.quantity <= 1} className="size-7 grid place-items-center text-fg-muted hover:text-fg disabled:opacity-30" aria-label={t("Menge verringern")}><Minus className="size-3.5" /></button>
                      <span className="w-8 text-center tabular-nums font-medium text-[13px]">{l.quantity}</span>
                      <button onClick={() => setQty(l.productId, l.quantity + 1)} className="size-7 grid place-items-center text-fg-muted hover:text-fg" aria-label={t("Menge erhöhen")}><Plus className="size-3.5" /></button>
                    </div>
                    <button onClick={() => setLines((ls) => ls.filter((x) => x.productId !== l.productId))} className="size-7 grid place-items-center rounded-lg text-fg-subtle hover:text-danger" aria-label={t("Komponente entfernen")}><Trash2 className="size-3.5" /></button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="relative">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Komponente hinzufügen (SKU, Name, EAN) …")} leading={<Search className="size-3.5" />} />
            {results.data && search.trim().length >= 2 && (
              <div className="absolute z-10 mt-1 w-full rounded-xl border border-border bg-surface shadow-xl p-1 animate-pop-in">
                {results.data.items.filter((c) => c.id !== p.id).length === 0 && <div className="px-3 py-2 text-fg-muted">{t("Kein Artikel gefunden")}</div>}
                {results.data.items.filter((c) => c.id !== p.id).map((c) => (
                  <button key={c.id} onClick={() => add(c)} disabled={!!c.bundle?.components.length} className="w-full flex items-center gap-2 h-9 px-2.5 rounded-lg hover:bg-surface-2 text-left disabled:opacity-50">
                    <span className="font-mono text-[12px] text-fg-muted w-32 truncate">{c.sku}</span>
                    <span className="truncate flex-1">{c.name}</span>
                    {c.bundle?.components.length ? <Badge>{t("Set")}</Badge> : null}
                  </button>
                ))}
              </div>
            )}
          </div>

          {(dirty || lines.length === 0) && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-[12px] text-fg-muted">{lines.length === 0 ? t("Noch keine Komponenten") : t("{length} Komponenten · {v} Teile pro Set", { length: lines.length, v: lines.reduce((n, l) => n + l.quantity, 0) })}</span>
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setLines(initial); if (!initial.length) setEditing(false); }}>{t("Abbrechen")}</Button>
                {(lines.length > 0 || initial.length > 0) && (
                  <Button size="sm" variant="primary" loading={saving} onClick={() => void save()}>{lines.length ? t("Stückliste speichern") : t("Stückliste entfernen")}</Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
